
import { browserAdapter } from '~/services/browserAdapter/browserAdapter';
import type { AuthTokens } from '../types';

/**
 * Token Storage
 * 
 * Manages secure storage of auth tokens using browserAdapter
 * 
 * STORAGE STRATEGY:
 * - Access token: In-memory only (fast, secure, lost on refresh)
 * - Refresh token: Stored in browserAdapter (persists, iframe-safe)
 * 
 * SECURITY:
 * - Access tokens never persisted (XSS protection)
 * - Refresh tokens are Supabase-signed JWTs (tamper-proof)
 * - Short-lived access tokens (1 hour) + backend verification
 * - Token rotation on every refresh (Supabase automatic)
 * - Industry standard approach (Auth0, Firebase, AWS Cognito)
 * 
 * TODO (HIGH PRIORITY): Add AES-GCM-256 encryption for refresh tokens
 * While signed JWTs are secure, client-side encryption adds defense-in-depth:
 * - Store encryption key in sessionStorage (lost on tab close)
 * - Encrypt refresh token before storing in localStorage
 * - Decrypt on retrieval
 * See: crypto.ts (to be implemented)
 */

// Storage keys
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const REFRESH_EXPIRY_KEY = 'auth_refresh_expiry';

// In-memory storage for access token
let accessToken: string | null = null;
let accessTokenExpiry: number | null = null;

/**
 * Store access token in memory only
 * 
 * @param token - Access token
 * @param expiresIn - Seconds until expiry
 */
export const setAccessToken = (token: string, expiresIn: number): void => {
  // ⚠️ VALIDATION: Check if token looks like a valid JWT
  if (!token || token.length < 50 || !token.includes('.')) {
    // Log technical details for debugging
    console.error('[TokenStorage] Invalid access token received', {
      length: token?.length || 0,
      hasDotsForJWT: token?.includes('.'),
      preview: token?.substring(0, 50)
    });
    // Throw user-friendly message
    throw new Error('Authentication failed. Please try logging in again.');
  }
  
  accessToken = token;
  // Convert relative seconds to absolute Unix timestamp
  accessTokenExpiry = Math.floor(Date.now() / 1000) + expiresIn;
};

/**
 * Get access token from memory
 * 
 * @returns Access token or null
 */
export const getAccessToken = (): string | null => {
  return accessToken;
};

/**
 * Get access token expiry timestamp
 * 
 * @returns Unix timestamp (seconds) or null
 */
export const getAccessTokenExpiry = (): number | null => {
  return accessTokenExpiry;
};

/**
 * Clear access token from memory
 */
export const clearAccessToken = (): void => {
  accessToken = null;
  accessTokenExpiry = null;
};

/**
 * Check if we have a valid access token in memory
 * 
 * @returns True if valid access token exists
 */
export const hasValidAccessToken = (): boolean => {
  if (!accessToken || !accessTokenExpiry) return false;
  
  const now = Math.floor(Date.now() / 1000);
  return accessTokenExpiry > now;
};

/**
 * Store refresh token in browserAdapter
 * 
 * Currently stores Supabase-signed JWT directly in localStorage.
 * JWTs are cryptographically signed and validated server-side.
 * 
 * TODO (HIGH PRIORITY): Add AES-GCM-256 encryption before storage
 * - Generate encryption key in sessionStorage
 * - Encrypt token before storing
 * - See crypto.ts (to be implemented)
 * 
 * @param token - Refresh token (Supabase JWT)
 * @param expiresIn - Seconds until expiry
 */
export const setRefreshToken = async (token: string, expiresIn: number): Promise<void> => {
  try {
    // ⚠️ VALIDATION: Accept both local and production Supabase tokens
    // - Local Supabase: Short random string (e.g., "imb7narglhkj", 8-20 chars)
    // - Production Supabase: JWT format (hundreds of characters with dots)
    if (!token || token.length < 8) {
      // Log technical details for debugging
      console.error('[TokenStorage] Invalid refresh token received', {
        length: token?.length || 0,
        preview: token?.substring(0, 20) + '...'
      });
      // Throw user-friendly message
      throw new Error('Authentication failed. Please try logging in again.');
    }
    
    // Calculate expiry timestamp
    const expiry = Math.floor(Date.now() / 1000) + expiresIn;
    
    // Store in browserAdapter (iframe-safe)
    await browserAdapter.storage.set(REFRESH_TOKEN_KEY, token);
    await browserAdapter.storage.set(REFRESH_EXPIRY_KEY, expiry.toString());
  } catch (error) {
    console.error('[TokenStorage] Failed to store refresh token:', error);
    throw error;
  }
};

/**
 * Get refresh token from browserAdapter
 * 
 * @returns Refresh token or null
 */
export const getRefreshToken = async (): Promise<string | null> => {
  try {
    const token = await browserAdapter.storage.get(REFRESH_TOKEN_KEY);
    
    if (!token) return null;
    
    // Check expiry
    const expiryStr = await browserAdapter.storage.get(REFRESH_EXPIRY_KEY);
    if (expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      const now = Math.floor(Date.now() / 1000);
      
      if (expiry < now) {
        await clearRefreshToken();
        return null;
      }
    }
    
    return token;
  } catch (error) {
    console.error('[TokenStorage] Failed to retrieve refresh token:', error);
    // Clear corrupted token
    await clearRefreshToken();
    return null;
  }
};

/**
 * Clear refresh token from browserAdapter
 */
export const clearRefreshToken = async (): Promise<void> => {
  try {
    await browserAdapter.storage.remove(REFRESH_TOKEN_KEY);
    await browserAdapter.storage.remove(REFRESH_EXPIRY_KEY);
  } catch (error) {
    console.error('[TokenStorage] Failed to clear refresh token:', error);
  }
};

/**
 * Store both access and refresh tokens
 * 
 * @param tokens - Access and refresh tokens with expiry
 */
export const setTokens = async (tokens: {
  accessToken: string;
  accessExpiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
}): Promise<void> => {
  // Store access token in memory
  setAccessToken(tokens.accessToken, tokens.accessExpiresIn);
  
  // Store refresh token in browserAdapter
  await setRefreshToken(tokens.refreshToken, tokens.refreshExpiresIn);
};

/**
 * Get current tokens (if valid)
 * 
 * @returns Tokens or null if none/expired
 */
export const getTokens = async (): Promise<AuthTokens | null> => {
  const access = getAccessToken();
  const accessExp = getAccessTokenExpiry();
  const refresh = await getRefreshToken();
  const refreshExpStr = await browserAdapter.storage.get(REFRESH_EXPIRY_KEY);
  
  if (!access || !accessExp || !refresh || !refreshExpStr) {
    return null;
  }
  
  return {
    accessToken: access,
    accessTokenExpiry: accessExp,
    refreshToken: refresh,
    refreshTokenExpiry: parseInt(refreshExpStr, 10),
  };
};

/**
 * Clear all tokens from storage
 * 
 * Call this on logout
 */
export const clearAllTokens = async (): Promise<void> => {
  clearAccessToken();
  await clearRefreshToken();
};

/**
 * Check if we have a refresh token stored
 * 
 * @returns True if refresh token exists (may be expired)
 */
export const hasRefreshToken = async (): Promise<boolean> => {
  const token = await browserAdapter.storage.get(REFRESH_TOKEN_KEY);
  return !!token;
};
